import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Plus, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Search, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatDate } from "../lib/constants";

/**
 * AdminClients — sortable, searchable table of client organisations.
 * Row click opens the detail page (card view). Inline edit via pencil
 * icon opens the existing modal. Delete enforces backend guard.
 */
export default function AdminClients() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data: clients } = await api.get("/clients");
      // Enrich with counts in parallel
      const enriched = await Promise.all(
        clients.map(async (c) => {
          try {
            const { data: detail } = await api.get(`/clients/${c.id}/detail`);
            return {
              ...c,
              user_count: detail.user_count,
              user_cap: detail.user_cap,
              active_project_count: detail.active_project_count,
              project_cap: detail.project_cap,
            };
          } catch (_e) {
            return { ...c, user_count: 0, user_cap: 3, active_project_count: 0, project_cap: 3 };
          }
        })
      );
      setRows(enriched);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, name) => {
    if (!confirm(`Delete ${name}? You must archive or delete their projects first.`)) return;
    try { await api.delete(`/clients/${id}`); load(); }
    catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const sorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) =>
          [r.name, r.contact_name, r.contact_email, r.contact_phone]
            .filter(Boolean).some((v) => v.toLowerCase().includes(q))
        )
      : rows;
    const key = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[key] ?? ""; const bv = b[key] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * dir;
    });
  }, [rows, query, sort]);

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  return (
    <div className="max-w-7xl mx-auto px-8 sm:px-12 py-12" data-testid="admin-clients-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-2">Practice</div>
          <h1 className="font-display text-4xl tracking-tight text-[color:var(--atech-text)]">Client organisations</h1>
          <div className="mt-2 text-sm text-[color:var(--atech-text-dim)]">
            {rows.length} organisation{rows.length === 1 ? "" : "s"}
          </div>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-copper flex items-center gap-2 text-sm" data-testid="new-client-button">
          <Plus size={16} /> New client
        </button>
      </div>

      {/* Search */}
      <div className="card-surface p-4 mb-5 flex items-center gap-2">
        <Search size={14} className="text-[color:var(--atech-text-muted)]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, contact, email, phone…"
          className="bg-transparent flex-1 text-sm focus:outline-none placeholder:text-[color:var(--atech-text-muted)]"
          data-testid="client-search-input"
        />
      </div>

      {err && <div className="text-[#F87171] text-sm mb-4">{err}</div>}

      {loading ? (
        <div className="label">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-surface p-14 text-center">
          <Building2 size={28} className="mx-auto text-[color:var(--atech-text-muted)]" />
          <div className="font-display text-2xl text-[color:var(--atech-text)] mt-4">No clients yet</div>
          <p className="text-sm text-[color:var(--atech-text-dim)] mt-2 mb-6">
            Create a client organisation to start assigning projects.
          </p>
          <button onClick={() => setShowNew(true)} className="btn-copper inline-flex items-center gap-2 text-sm">
            <Plus size={16} /> New client
          </button>
        </div>
      ) : (
        <div className="card-surface overflow-hidden" data-testid="clients-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <Th label="Name" k="name" sort={sort} onSort={toggleSort} />
                  <Th label="Contact" k="contact_name" sort={sort} onSort={toggleSort} />
                  <Th label="Email" k="contact_email" sort={sort} onSort={toggleSort} />
                  <Th label="Users" k="user_count" sort={sort} onSort={toggleSort} align="right" />
                  <Th label="Active projects" k="active_project_count" sort={sort} onSort={toggleSort} align="right" />
                  <Th label="Created" k="created_at" sort={sort} onSort={toggleSort} />
                  <th className="px-5 py-3 text-right label w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--atech-border)]">
                {sorted.map((c) => (
                  <tr
                    key={c.id}
                    className="group hover:bg-[color:var(--atech-surface-hover)]/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/clients/${c.id}`)}
                    data-testid={`client-row-${c.id}`}
                  >
                    <td className="px-5 py-4">
                      <div className="font-display text-base text-[color:var(--atech-text)] group-hover:text-[color:var(--atech-copper)] transition-colors">
                        {c.name}
                      </div>
                      {c.is_demo && (
                        <div className="text-[9px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)] mt-0.5">Demo</div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-[color:var(--atech-text-dim)]">{c.contact_name || "—"}</td>
                    <td className="px-5 py-4 text-[color:var(--atech-text-dim)] font-mono text-[12px]">{c.contact_email || "—"}</td>
                    <td className="px-5 py-4 text-right font-mono text-[12px]">
                      <span className="text-[color:var(--atech-text)]">{c.user_count}</span>
                    </td>
                    <td className="px-5 py-4 text-right font-mono text-[12px]">
                      <span className="text-[color:var(--atech-text)]">{c.active_project_count}</span>
                    </td>
                    <td className="px-5 py-4 text-[color:var(--atech-text-muted)] text-[12px]">{formatDate(c.created_at)}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setEditing(c)} title="Edit" className="p-2 text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:bg-[color:var(--atech-surface-hover)] rounded-[2px]" data-testid={`edit-client-${c.id}`}>
                          <Pencil size={14} />
                        </button>
                        <Link to={`/admin/clients/${c.id}`} title="Open" className="p-2 text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-copper)] hover:bg-[color:var(--atech-surface-hover)] rounded-[2px] inline-flex" data-testid={`open-client-${c.id}`}>
                          →
                        </Link>
                        <button onClick={() => remove(c.id, c.name)} title="Delete" className="p-2 text-[color:var(--atech-text-dim)] hover:text-[#F87171] hover:bg-[color:var(--atech-surface-hover)] rounded-[2px]" data-testid={`delete-client-${c.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showNew && <ClientModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <ClientModal client={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Th({ label, k, sort, onSort, align }) {
  const active = sort.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-5 py-3 label cursor-pointer select-none ${align === "right" ? "text-right" : "text-left"}`} onClick={() => onSort(k)}>
      <span className={`inline-flex items-center gap-1.5 ${active ? "text-[color:var(--atech-copper)]" : ""}`}>
        {label} <Icon size={11} />
      </span>
    </th>
  );
}

function ClientModal({ client, onClose, onSaved }) {
  const editing = !!client;
  const [form, setForm] = useState({
    name: client?.name || "",
    contact_name: client?.contact_name || "",
    contact_email: client?.contact_email || "",
    contact_phone: client?.contact_phone || "",
    notes: client?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (editing) await api.patch(`/clients/${client.id}`, form);
      else await api.post("/clients", form);
      onSaved();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  const inputCls = "w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)]";
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-surface w-full max-w-md p-8 fade-up" onClick={(e) => e.stopPropagation()} data-testid="client-modal">
        <h3 className="font-display text-xl text-[color:var(--atech-text)] mb-5">
          {editing ? "Edit client" : "New client organisation"}
        </h3>
        <form onSubmit={submit} className="space-y-4">
          <div><span className="label block mb-2">Name *</span><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="client-name-input" /></div>
          <div><span className="label block mb-2">Contact name</span><input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} /></div>
          <div><span className="label block mb-2">Contact email</span><input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inputCls} /></div>
          <div><span className="label block mb-2">Contact phone</span><input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inputCls} /></div>
          <div><span className="label block mb-2">Notes</span><textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-none`} /></div>
          {err && <div className="text-xs text-[#F87171]">{err}</div>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
            <button type="submit" disabled={busy} className="btn-copper text-sm" data-testid="client-submit">
              {busy ? "Saving…" : (editing ? "Save" : "Create client")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
